import React from 'react';
import Link from 'next/link';
import { Target, Clock, BookOpen, Zap, CheckCircle2 } from 'lucide-react';
import { MOCK_RESULTS } from './MockExamScreen';

interface Props {
  results: typeof MOCK_RESULTS;
  gapToTarget: number;
}

const REVISION_ITEMS = [
  {
    id: 'rev-mh',
    priority: 1,
    type: 'Matching Headings',
    currentAccuracy: 50,
    targetAccuracy: 75,
    currentTime: 121,
    targetTime: 100,
    bandImpact: '+1–2 raw marks',
    sessions: 3,
    actions: [
      'Complete 3 × isolated Matching Headings drills (12 questions each)',
      'Focus: skim paragraph for main idea before reading heading list',
      'Annotated walkthrough: review 3 Matching Headings walkthroughs flagged as "confused"',
    ],
    href: '/practice-session',
    urgent: true,
  },
  {
    id: 'rev-ynng',
    priority: 2,
    type: 'Yes / No / Not Given',
    currentAccuracy: 60,
    targetAccuracy: 80,
    currentTime: 106,
    targetTime: 90,
    bandImpact: '+1 raw mark',
    sessions: 2,
    actions: [
      'Complete the dedicated False vs. Not Given deep-dive module',
      'Practice identifying author\'s hedging language as a No view',
      'Spaced-repetition: re-attempt 5 YN/NG questions from Week 2',
    ],
    href: '/annotated-walkthrough',
    urgent: true,
  },
  {
    id: 'rev-tfng',
    priority: 3,
    type: 'True / False / Not Given',
    currentAccuracy: 63,
    targetAccuracy: 80,
    currentTime: 97,
    targetTime: 88,
    bandImpact: '+1 raw mark',
    sessions: 2,
    actions: [
      'Drill: always read the complete sentence before deciding',
      'Focus on negation patterns: "not," "without," "no equivalent," "fails to"',
      'Complete TF/NG distractor-confusion drill (10 questions)',
    ],
    href: '/practice-session',
    urgent: false,
  },
  {
    id: 'rev-time',
    priority: 4,
    type: 'Passage Pacing',
    currentAccuracy: null,
    targetAccuracy: null,
    currentTime: 89,
    targetTime: 80,
    bandImpact: 'Prevents time-pressure errors on Passage 3',
    sessions: 2,
    actions: [
      'Practise passage-triage: complete Passage 1 in 17 min, bank 3 min for Passage 3',
      'Apply 75-second decision cap — mark and move on any question exceeding it',
      'Run 2 × timed Passage 3 drills at 22-minute target',
    ],
    href: '/mock-exam-performance-report',
    urgent: false,
  },
];

export default function ReportRevisionPlan({ results: _results, gapToTarget }: Props) {
  return (
    <div className="space-y-6 animate-slide-up">
      {/* Summary */}
      <div className="card-base p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Target size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="section-header mb-1">Last-Mile Revision Plan</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You need <span className="font-bold text-primary">{gapToTarget} more correct answer{gapToTarget !== 1 ? 's' : ''}</span> to reach Band 8.0.
              The plan below identifies the specific question types and actions that will deliver those marks in the next 7 days.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          {[
            { id: 'tl-days', label: 'Days remaining', value: '28', icon: <Clock size={14} className="text-warning" />, color: 'text-warning' },
            { id: 'tl-sessions', label: 'Sessions needed', value: '9', icon: <Zap size={14} className="text-primary" />, color: 'text-primary' },
            { id: 'tl-marks', label: 'Marks to close', value: String(gapToTarget), icon: <Target size={14} className="text-accent" />, color: 'text-accent' },
          ].map((tl) => (
            <div key={tl.id} className="p-3.5 rounded-xl bg-muted border border-border text-center">
              <div className="flex justify-center mb-1.5">{tl.icon}</div>
              <p className={`text-2xl font-bold tabular-nums ${tl.color}`}>{tl.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tl.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Revision items */}
      <div className="space-y-4">
        {REVISION_ITEMS.map((item) => (
          <div
            key={item.id}
            className={`card-base p-5 ${item.urgent ? 'border-l-4 border-l-danger' : ''}`}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  item.priority === 1 ? 'bg-danger text-danger-foreground' :
                  item.priority === 2 ? 'bg-warning text-warning-foreground': 'bg-muted text-muted-foreground'
                }`}>
                  {item.priority}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{item.type}</h3>
                  <p className="text-xs text-muted-foreground">{item.sessions} sessions recommended · Band impact: <span className="font-semibold text-accent">{item.bandImpact}</span></p>
                </div>
              </div>
              {item.urgent && (
                <span className="text-xs font-bold text-danger bg-danger-bg border border-danger/30 px-2.5 py-1 rounded-full flex-shrink-0">
                  Priority
                </span>
              )}
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {item.currentAccuracy !== null && (
                <div className="p-3 rounded-lg bg-muted border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Accuracy gap</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-danger tabular-nums">{item.currentAccuracy}%</span>
                    <span className="text-xs text-muted-foreground">→</span>
                    <span className="text-sm font-bold text-accent tabular-nums">{item.targetAccuracy}%</span>
                  </div>
                </div>
              )}
              <div className="p-3 rounded-lg bg-muted border border-border">
                <p className="text-xs text-muted-foreground mb-1">Time gap</p>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold tabular-nums ${item.currentTime > 90 ? 'text-warning' : 'text-foreground'}`}>{item.currentTime}s</span>
                  <span className="text-xs text-muted-foreground">→</span>
                  <span className="text-sm font-bold text-accent tabular-nums">{item.targetTime}s</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2 mb-4">
              {item.actions.map((action, i) => (
                <div key={`${item.id}-action-${i}`} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">{i + 1}</span>
                  </div>
                  <p className="text-xs text-foreground leading-relaxed">{action}</p>
                </div>
              ))}
            </div>

            <Link
              href={item.href}
              className="btn-primary py-2 px-4 text-xs inline-flex items-center gap-1.5"
            >
              {item.type === 'Matching Headings' || item.type === 'Passage Pacing' ? (
                <><Zap size={12} /> Start Drill</>
              ) : (
                <><BookOpen size={12} /> Start Walkthrough</>
              )}
            </Link>
          </div>
        ))}
      </div>

      {/* When ready */}
      <div className="card-base p-5 bg-success-bg border-accent/30">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={20} className="text-accent flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-foreground">After completing this revision plan</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Return here to take Mock Exam 2. If your raw score reaches 35+, you are on track for Band 8.0 on exam day.
            </p>
          </div>
        </div>
        <Link href="/mock-exam-performance-report" className="btn-primary mt-4 inline-flex items-center gap-2">
          Schedule Mock Exam 2
        </Link>
      </div>
    </div>
  );
}